import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import { hydraUiApi } from '@/services/api';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { accountKeys } from '@/services/db/account/entity';
import { deleteCrossAppCacheByPrefix } from '@/utils/cross-app-cache';
import { sentryCaptureException } from '@/utils/sentry';

/**
 * Refreshes the account fields a retry changes, so the overdue chrome does not stay stale.
 *
 * Delete the cache entries before you invalidate: they are `cacheOnlyOnce`, so an invalidation
 * alone refetches the pre-retry payload. Delete both prefixes, because a billing path has no
 * workspace segment and so caches under `/account_by_environment`, not `/accounts`.
 *
 * This function must not reject. A rejection reaches the mutation `onError`, which then shows a
 * payment failure after the success.
 */
async function reloadAfterRetryPayment(queryClient: QueryClient, accountId: number): Promise<void> {
  deleteCrossAppCacheByPrefix('/accounts');
  deleteCrossAppCacheByPrefix('/account_by_environment');
  try {
    await Promise.all([
      billing.initialize.perform({ refresh: true, ignoreErrors: true }),
      queryClient.invalidateQueries({ queryKey: ['authenticated'] }),
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId, String(accountId)) }),
    ]);
  } catch (error) {
    // A lost refresh only causes stale chrome until the next navigation, but stays visible here.
    sentryCaptureException(error instanceof Error ? error : new Error('Post-retry billing reload failed'));
  }
}

export type RetryPayment = {
  retryPayment: () => void;
  isRetrying: boolean;
};

export function useRetryPayment(isOrdwayCustomer: boolean): RetryPayment {
  const { account } = useAuth();
  const snackbars = useSnackbars();
  const queryClient = useQueryClient();

  const retryPaymentMutation = useMutation({
    mutationFn: () => hydraUiApi.billing.retryPayment({ params: { path: { account_id: String(account.id) } } }),
    onSuccess: async () => {
      snackbars.create({
        variant: 'default',
        id: 'account.retryPayment.success',
        message: isOrdwayCustomer ? 'Payment will be processed soon.' : 'Payment processed.',
      });
      await reloadAfterRetryPayment(queryClient, account.id);
    },
    onError: () => {
      snackbars.create({
        variant: 'critical',
        id: 'account.retryPayment.failure',
        message: "Your payment couldn't be processed. Contact win@customer.io for support.",
      });
    },
  });

  return {
    retryPayment: () => retryPaymentMutation.mutate(),
    isRetrying: retryPaymentMutation.isPending,
  };
}
